import {makeAutoObservable} from "mobx";
import {SelectChangeEvent} from "@mui/material";
import {ChangeEvent} from "react";
import {ContactFormEntry} from "../models/ContactFormEntry";
import agent from "../api/agent";
import {ADD} from "mobx/dist/types/observablemap";

export default class ContactFormStore {
    public sendFurtherInformation: boolean = false;
    public contactMeForAppointment: boolean = false;
    public interestedInRenting: boolean = false;

    public title: 'Mr.' | 'Ms.' = "Mr."
    public firstName: string = '';
    public lastName: string = '';
    public street: string = '';
    public houseNumber: string = '';
    public zip: number = 0;
    public city: string = '';
    public phone: string = '';
    public mobile: string = '';
    public email: string = '';
    public preferredMethodOfContact: 'email' | 'mobile' | 'phone' = 'email';

    public firstNameIsValid: boolean = true;
    public lastNameIsValid: boolean = true;
    public emailIsValid: boolean = true;
    public phoneIsValid: boolean = true;
    public mobileIsValid: boolean = true;

    public additionalInformation: string = '';

    public isLoading: boolean = false;

    constructor() {
        makeAutoObservable(this)
    }

    isEmailValid(): boolean {
        // Regular expression pattern for a basic email validation
        const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        const isValid = emailPattern.test(this.email);
        this.emailIsValid = isValid;
        return isValid;
    }

    isFirstNameValid(): boolean {
        let isValid = true;
        if(this.firstName === '' || this.firstName === undefined || this.firstName === null) isValid = false;
        this.firstNameIsValid = isValid;
        return isValid;
    }

    isLastNameValid(): boolean {
        let isValid = true;
        if(this.lastName === '' || this.lastName === undefined || this.lastName === null) isValid = false;
        this.lastNameIsValid = isValid;
        return isValid;
    }

    public isFormValid = (): boolean => {
        let isValid = true;
        if(!this.isFirstNameValid()) isValid = false;
        if(!this.isLastNameValid()) isValid = false;
        if(this.preferredMethodOfContact === 'email') {
            this.phoneIsValid = true;
            this.mobileIsValid = true;
            if(!this.isEmailValid()) isValid = false;
        }
        if(this.preferredMethodOfContact === 'mobile') {
            this.emailIsValid = true;
            this.phoneIsValid = true;
            if(this.mobile.trim().length === 0) {
                this.mobileIsValid = false;
                isValid = false;
            }
        }
        if(this.preferredMethodOfContact === 'phone') {
            this.emailIsValid = true;
            this.mobileIsValid = true;
            if(this.phone.trim().length === 0) {
                this.phoneIsValid = false;
                isValid = false;
            }
        }
        return isValid;
    }

    public setSendFurtherInformation = (value: boolean) => {
        this.sendFurtherInformation = value;
    }
    public setContactMeForAppointment = (value: boolean) => {
        this.contactMeForAppointment = value;
    }
    public setInterestedInRenting = (value: boolean) => {
        this.interestedInRenting = value;
    }
    public setTitle = (event: SelectChangeEvent<"Mr." | "Ms.">) => {
        //@ts-ignore
        this.title = event.target.value
    }
    public setFirstName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.firstName = event.target.value;
        this.isFirstNameValid();
    }
    public setLastName = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.lastName = event.target.value;
        this.isLastNameValid();
    }
    public setStreet = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.street = event.target.value;
    }
    public setHouseNumber = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.houseNumber = event.target.value;
    }
    public setZip = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.zip = parseInt(event.target.value);
    }
    public setCity = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.city = event.target.value;
    }
    public setPhone = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.phone = event.target.value;
    }
    public setMobile = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.mobile = event.target.value;
    }
    public setEmail = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.email = event.target.value;
        this.isEmailValid();
    }
    public setPreferredMethodOfContact = (event: SelectChangeEvent<'email'|'phone'|'mobile'>) => {
        //@ts-ignore
        this.preferredMethodOfContact = event.target.value;
        this.isFormValid();
    }
    public setAdditionalInformationFromEvent = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.additionalInformation = event.target.value;
    }
    
    public setAdditionalInformationFromString = (additionalInformation: string) => {
        this.additionalInformation = additionalInformation;
    }

    public setIsLoading = (value: boolean) => {
        this.isLoading = value;
    }

    private createContactFormEntry = (currentLanguage: 'ru' | 'ge' | 'en' | 'fr', token: string): ContactFormEntry => {
        return {
            id: 0,
            title: this.title,
            firstName: this.firstName,
            lastName: this.lastName,
            street: this.street,
            houseNumber: this.houseNumber,
            zip: this.zip,
            city: this.city,
            phone: this.phone,
            mobile: this.mobile,
            email: this.email,
            preferredMethodOfContact: this.preferredMethodOfContact,
            additionalInformation: this.additionalInformation,
            createdAt: new Date(Date.now()),
            sent: false,
            error: undefined,
            language: currentLanguage,
            sendFurtherInformation: this.sendFurtherInformation,
            contactMeForAppointment: this.contactMeForAppointment,
            interestedInRenting: this.interestedInRenting,
            token: token,
        }
    }

    public sendForm = async (currentLanguage: 'ru' | 'ge' | 'en' | 'fr', token: string) => {
        if(!this.isFormValid()) return;
        this.setIsLoading(true);
        await agent.ContactForm.post(this.createContactFormEntry(currentLanguage, token));
        this.setIsLoading(false);
    }
}